// The parts of a signed-in client, created at sign-in and torn down at sign-out.

import { createContext, useContext } from "react";
import type { MessageResponse } from "../api/contract.gen.ts";
import type { Session } from "../auth/session.ts";
import { Player } from "../chats/player.ts";
import { ContentKind, encrypt, generateKey } from "../crypto/contentCipher.ts";
import { clearAvatars } from "../data/avatars.ts";
import { encodeUtf8, toBase64 } from "../data/bytes.ts";
import { clearMedia, primeMedia } from "../data/media.ts";
import type { Word } from "../audio/liveTranscript.ts";
import type { Recording } from "../audio/recorder.ts";
import { attachVideo, type Sent, sendFile, sendVoice } from "../data/upload.ts";
import type { VideoResult } from "../audio/videoTrack.ts";
import { decryptContent, type Gif } from "../data/content.ts";
import { type Store, type StoredMessage } from "../data/store.ts";
import { SyncEngine } from "../data/sync.ts";
import { disable } from "../push/webPush.ts";
import { Realtime } from "../realtime/realtime.ts";
import { CallController } from "../calls/controller.ts";
import { decryptPosition, LiveShare } from "../location/live.ts";
import type { Place } from "../data/content.ts";

export class Services {
  readonly sync: SyncEngine;
  readonly realtime: Realtime;
  readonly player: Player;
  readonly calls: CallController;
  /** Live locations this tab shares, by message id. */
  readonly sharing = new Map<string, LiveShare>();
  readonly session: Session;
  readonly store: Store;

  constructor(session: Session, store: Store) {
    this.session = session;
    this.store = store;
    store.me = session.current?.user.id ?? null;
    this.sync = new SyncEngine(session, store);
    this.realtime = new Realtime({ token: () => session.token() });
    this.calls = new CallController(session, () => store.me, (title, body, conversationId) => void showSystemNotification(title, body, conversationId));
    this.sync.onActiveCalls = (calls) => this.calls.onRunning(calls ?? []);
    this.player = new Player((track) => void session.call(`/v1/messages/${track.messageId}/heard`, { method: "POST", json: {} }).catch(() => undefined));
  }

  start() {
    this.session.beforeLogout = () => disable(this.session);
    this.realtime.subscribe((frame) => this.sync.onFrame(frame));
    this.realtime.subscribe((frame) => this.calls.onFrame(frame));
    this.realtime.subscribe((frame) => {
      if (frame.t === "location_watch") for (const s of this.sharing.values()) s.wake();
      if (frame.t === "location") void this.onPosition(frame.conversation as string, frame.share as string, frame.position as string, frame.at as number);
    });
    this.realtime.start();
    void this.sync.start().catch(() => undefined);
  }

  stop() {
    void this.calls.hangup();
    for (const s of this.sharing.values()) void s.stop();
    this.sharing.clear();
    this.player.stop();
    this.realtime.stop();
    clearMedia();
    clearAvatars();
    this.store.clear();
  }

  /** Fresh links for [message]: its conversation's newest messages again (the app does the same). */
  async refresh(message: StoredMessage): Promise<StoredMessage | undefined> {
    await this.sync.loadMessages(message.response.conversation_id);
    return this.store.message(message.response.conversation_id, message.response.id);
  }

  /** Sends a text, encrypted with a new key; shown at once, the sync brings the server's version. */
  async sendText(conversationId: string, text: string, extra: Record<string, unknown> = {}): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) return;
    const mck = generateKey();
    const body = await encrypt(ContentKind.TEXT, encodeUtf8(trimmed), mck);
    const response = await this.session.call<MessageResponse>("/v1/messages", {
      method: "POST",
      json: { client_id: crypto.randomUUID(), conversation_id: conversationId, kind: "text", body: toBase64(body), mck: toBase64(mck), ...extra },
    });
    this.store.putMessage({ response, mck, content: { text: trimmed } });
    // The list shows it at once; the next sync brings the server's version of the conversation.
    const c = this.store.conversations.get(conversationId);
    if (c) {
      const last = { id: response.id, sender_id: response.sender_id, kind: "text", duration_ms: 0, created_at: response.created_at };
      this.store.conversations.set(conversationId, { ...c, last_message: last, last_activity_at: response.created_at });
    }
    this.store.changed();
  }

  /** [video]: the silent video track, attached after the voice message is in (it can fail on its own). */
  async sendVoice(conversationId: string, recording: Recording, words?: Word[] | null, video?: VideoResult | null): Promise<void> {
    const sent = await sendVoice(this.session, conversationId, recording, words);
    primeMedia(sent.response.id, "audio", new Blob([recording.ogg], { type: "audio/ogg" }));
    await this.putSent(conversationId, sent, "voice");
    if (video) {
      await attachVideo(this.session, conversationId, sent, video);
      primeMedia(sent.response.id, "media", video.blob);
      if (video.thumbnail) primeMedia(sent.response.id, "thumb", new Blob([video.thumbnail], { type: "image/jpeg" }));
      // The server's version (with media and segments) comes with the next sync.
      void this.sync.run().catch(() => undefined);
    }
  }

  /** [replyTo]: the message this one answers (an edited picture answers its original). */
  async sendFile(conversationId: string, file: File, caption?: string, replyTo?: string): Promise<void> {
    const sent = await sendFile(this.session, conversationId, file, caption, replyTo ? { reply_to: { message_id: replyTo } } : {});
    if (sent.response.kind === "image" || sent.response.kind === "file") primeMedia(sent.response.id, "media", file);
    await this.putSent(conversationId, sent, sent.response.kind ?? "file");
  }

  /** A picture for a canvas: an image message with board_id (it shows on the canvas, not in the chat). */
  async sendCanvasImage(conversationId: string, boardId: string, file: File): Promise<Sent> {
    const sent = await sendFile(this.session, conversationId, file, undefined, { board_id: boardId });
    primeMedia(sent.response.id, "media", file);
    primeMedia(sent.response.id, "thumb", file);
    this.store.putMessage({ response: sent.response, mck: sent.mck, content: {} });
    this.store.changed();
    return sent;
  }

  /** Shows a message this browser just sent, before the sync brings it. */
  private async putSent(conversationId: string, sent: Sent, kind: string) {
    this.store.putMessage({ response: sent.response, mck: sent.mck, content: await decryptContent(sent.response, sent.mck) });
    const c = this.store.conversations.get(conversationId);
    if (c) {
      const r = sent.response;
      this.store.conversations.set(conversationId, { ...c, last_message: { id: r.id, sender_id: r.sender_id, kind, duration_ms: r.duration_ms, created_at: r.created_at }, last_activity_at: r.created_at });
    }
    this.store.changed();
  }

  /** A GIF from KLIPY: only its URLs and size go into the message, encrypted like a text. */
  async sendGif(conversationId: string, gif: Gif): Promise<void> {
    const mck = generateKey();
    const body = await encrypt(ContentKind.GIF, encodeUtf8(JSON.stringify(gif)), mck);
    const response = await this.session.call<MessageResponse>("/v1/messages", {
      method: "POST",
      json: { client_id: crypto.randomUUID(), conversation_id: conversationId, kind: "gif", body: toBase64(body), mck: toBase64(mck) },
    });
    await this.putSent(conversationId, { response, mck }, "gif");
  }

  /** A place (the current position or a pin on the map), encrypted like a text. */
  async sendPlace(conversationId: string, place: Place): Promise<void> {
    const mck = generateKey();
    const body = await encrypt(ContentKind.LOCATION, encodeUtf8(JSON.stringify(place)), mck);
    const response = await this.session.call<MessageResponse>("/v1/messages", { method: "POST", json: { client_id: crypto.randomUUID(), conversation_id: conversationId, kind: "location", body: toBase64(body), mck: toBase64(mck) } });
    await this.putSent(conversationId, { response, mck }, "location");
  }

  /** Starts sharing the live location from this tab; [durationS] null: until stopped. */
  async startLiveLocation(conversationId: string, durationS: number | null): Promise<void> {
    const mck = generateKey();
    const body = await encrypt(ContentKind.LOCATION, encodeUtf8(JSON.stringify({ live: true })), mck);
    const response = await this.session.call<MessageResponse>("/v1/messages", {
      method: "POST",
      json: { client_id: crypto.randomUUID(), conversation_id: conversationId, kind: "location", body: toBase64(body), mck: toBase64(mck), live: { duration_s: durationS, track: false } },
    });
    const share = new LiveShare(this.session, response.id, mck);
    share.onEnd = () => (this.sharing.delete(response.id), this.store.changed());
    share.onSent = (p) => {
      this.store.positions.set(response.id, { lat: p.lat, lon: p.lon, acc: p.acc, at: p.t });
      this.store.changed();
    };
    this.sharing.set(response.id, share);
    await this.putSent(conversationId, { response, mck }, "location");
    share.start();
  }

  async stopLiveLocation(messageId: string): Promise<void> {
    const share = this.sharing.get(messageId);
    this.sharing.delete(messageId);
    this.store.changed();
    if (share) await share.stop();
    else await this.session.call(`/v1/locations/${messageId}/stop`, { method: "POST" }).catch(() => undefined);
    void this.sync.run().catch(() => undefined);
  }

  /** A position of someone's live location, from the socket or the message itself. */
  async onPosition(conversationId: string, shareId: string, position: string | null | undefined, at?: number) {
    const m = this.store.message(conversationId, shareId);
    if (!position || !m?.mck) return;
    const p = await decryptPosition(position, m.mck);
    if (!p) return;
    const known = this.store.positions.get(shareId);
    if (known && known.at > (at ?? p.t)) return;
    this.store.positions.set(shareId, { lat: p.lat, lon: p.lon, acc: p.acc, at: at ?? p.t });
    this.store.changed();
  }

  async markRead(conversationId: string): Promise<void> {
    const c = this.store.conversations.get(conversationId);
    if (c && c.unread_count > 0) {
      this.store.conversations.set(conversationId, { ...c, unread_count: 0 });
      this.store.changed();
    }
    await this.session.call(`/v1/conversations/${conversationId}/read`, { method: "POST" }).catch(() => undefined);
  }
}

export const ServicesContext = createContext<Services | null>(null);

export function useServices(): Services {
  const services = useContext(ServicesContext);
  if (!services) throw new Error("not signed in");
  return services;
}

/** A notification while the tab is in the background (a ringing call), if the browser allows them. */
async function showSystemNotification(title: string, body: string, conversationId: string) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const registration = await navigator.serviceWorker?.getRegistration("/");
  const options = { body, tag: "voxie-call", icon: "/favicon.svg", data: { url: `/c/${conversationId}` }, requireInteraction: true };
  if (registration) await registration.showNotification(title, options);
  else new Notification(title, options);
}
